import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { withTranslation } from 'language/i18n';
import { getObjectByLanguage } from 'language/helpers';
import VideoWithThumbnail from 'components/VideoWithThumbnail';

const Gallery = ({
  t, galleryContent, setGalleryContent, mediasGroupedByCategoryName, machineTitle,
}) => {
  const mainSliderRef = useRef();
  const imageListSliderRef = useRef();
  const [mainCurrentIndex, setMainCurrentIndex] = useState(1);
  const [playingVideoPlayerRefs, setPlayingVideoPlayerRefs] = useState([]);

  const pausePlayingVideoPlayers = () => {
    playingVideoPlayerRefs.forEach((playerRef) => {
      playerRef.current.pause();
    });
    setPlayingVideoPlayerRefs([]);
  };

  const slideNext = (ref) => {
    ref.current.slickNext();
    pausePlayingVideoPlayers();
  };

  const slidePrevious = (ref) => {
    ref.current.slickPrev();
    pausePlayingVideoPlayers();
  };

  const slideGoTo = (ref, index) => {
    ref.current.slickGoTo(index);
    pausePlayingVideoPlayers();
  };

  const handleKeys = (event) => {
    if (event.keyCode === 27) {
      setGalleryContent({});
    }

    if (event.keyCode === 39) {
      slideNext(mainSliderRef);
    }

    if (event.keyCode === 37) {
      slidePrevious(mainSliderRef);
    }
  };

  useEffect(() => {
    document?.addEventListener('keydown', handleKeys);
    const body = document?.getElementsByClassName('body');
    body?.[0]?.classList?.add('stop-scrolling');
    if (mainSliderRef?.current) {
      slideGoTo(mainSliderRef, galleryContent?.showIndex || 0);
      setMainCurrentIndex((galleryContent?.showIndex || 0) + 1);
    }

    return () => {
      document?.removeEventListener('keydown', handleKeys);
      body?.[0]?.classList?.remove('stop-scrolling');
    };
  }, [galleryContent]);

  const mainSliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (current) => setMainCurrentIndex(current + 1),
  };

  const imageListSliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: false,
    speed: 500,
    slidesToShow: galleryContent?.medias?.length < 5 ? galleryContent.medias.length : 5,
    slidesToScroll: 1,
  };

  return (
    <div className={`im-image-gallery-extended ${!_.isEmpty(galleryContent) ? 'im-image-gallery-extended--active' : ''}`}>
      <div className="container">
        <div className="row">
          <div className="col">
            <div className="im-image-gallery-extended__view">
              <div className="im-slider-container">
                <div
                  className="im-slider im-image-gallery-extended__view__slider"
                  data-slider-id="productGalleryExtended"
                >
                  <Slider {...mainSliderSettings} ref={mainSliderRef}>
                    {
                      _.map(galleryContent?.medias, (media, index) => (
                        <div
                          className="im-image-gallery-extended__view__slider__item"
                          key={index}
                        >
                          {media.type === 'video' ? (
                            <VideoWithThumbnail
                              video={media}
                              layout="fill"
                              objectFit="contain"
                              onPlay={(playerRef) => setPlayingVideoPlayerRefs((current) => [playerRef, ...current])}
                              onPause={(playerRef) => setPlayingVideoPlayerRefs((current) => current.filter((i) => i !== playerRef))}
                            />
                          ) : (
                            <Image
                              src={media.main}
                              layout="fill"
                              objectFit="scale-down"
                            />
                          )}
                        </div>
                      ))
                    }
                  </Slider>
                </div>
                <div className="im-slider__arrows im-slider__arrows--type3">
                  <div className="im-slider__arrows__prev" onClick={() => slidePrevious(mainSliderRef)}>
                    <i className="fa-solid fa-angle-left" />
                  </div>
                  <div className="text-center">
                    <div className="fs-13 fw-900 mb-1 text-white">{_.result(galleryContent.medias, `[${mainCurrentIndex - 1}].category`, galleryContent?.title || 'GENEL GÖRÜNÜM')}</div>
                    <div className="fs-12 text-white mb-2 mt-1">{_.result(galleryContent.medias, `[${mainCurrentIndex - 1}].question`, '')}</div>
                    <div className="im-slider__counter__count text-yellow2 fs-12">{`${mainCurrentIndex}/${galleryContent.medias.length}`}</div>
                  </div>
                  <div className="im-slider__arrows__next" onClick={() => slideNext(mainSliderRef)}>
                    <i className="fa-solid fa-angle-right" />
                  </div>
                </div>
              </div>
            </div>
            <div className="im-image-gallery-extended__nav">
              {galleryContent.medias.length > 1 && (
                <div className="im-slider-container">
                  <div
                    className="im-slider im-image-gallery-extended__nav__slider"
                    data-slider-id="productGalleryExtendedNav"
                  >
                    <Slider {...imageListSliderSettings} ref={imageListSliderRef}>
                      {
                        _.map(galleryContent.medias, (media, index) => (
                          <a className="im-image-gallery-extended__nav__slider__item" key={index} onClick={() => slideGoTo(mainSliderRef, index)}>
                            {media.type === 'video' ? (
                              <VideoWithThumbnail
                                video={media}
                                layout="fill"
                                objectFit="cover"
                                notPlayable
                                smallerPlayButton
                              />
                            ) : (
                              <Image
                                src={media.thumbnail}
                                layout="fill"
                                objectFit="cover"
                              />
                            )}
                          </a>
                        ))
                      }
                    </Slider>
                  </div>
                  <div className="im-slider__arrows im-slider__arrows--type2">
                    <div className="im-slider__arrows__prev" onClick={() => slidePrevious(imageListSliderRef)}>
                      <i className="fa-solid fa-angle-left" />
                    </div>
                    <div className="im-slider__arrows__next" onClick={() => slideNext(imageListSliderRef)}>
                      <i className="fa-solid fa-angle-right" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row mt-3 mb-4">
          <div className="col">
            <div className="separator separator--horizontal" />
          </div>
        </div>
        <div className="row align-items-center mb-2">
          <div className="col-15 col-md-auto mb-2 mb-sm-0">
            <h2 className="fs-15 fw-900">
              {getObjectByLanguage(machineTitle)}
            </h2>
          </div>
          <div className="col-15 col-md align-items-center mb-2 mb-sm-0">
            <svg className="wh-15 inline-block mr-1">
              <use xlinkHref="/image/icons.svg#ic-safemark" />
            </svg>
            <span className="fs-11 fw-700">{t('underTheGuaranteeOfIyiMakina')}</span>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <span className="fs-12">{t('underTheGuaranteeOfIyiMakinaAlert')}</span>
          </div>
        </div>
        <div className="row mt-4 mb-4 d-none d-lg-flex">
          {_.map(Object.keys(mediasGroupedByCategoryName), (categoryName) => (
            mediasGroupedByCategoryName[categoryName].length > 0 && (
              <div
                className="col-5 list-item-v"
                key={categoryName}
                onClick={() => setGalleryContent({
                  medias: mediasGroupedByCategoryName[categoryName],
                })}
              >
                <a className="fs-12 fw-700 text-gray3">
                  {categoryName}
                  {' '}
                  <span className="fw-400">{`(${mediasGroupedByCategoryName[categoryName].length})`}</span>
                </a>
              </div>
            )
          ))}
        </div>
      </div>
      <div className="im-image-gallery-extended__close" onClick={() => setGalleryContent({})}>
        <i className="fa-solid fa-times" />
        <div className="fs-12 fw-500 text-gray7">ESC</div>
      </div>
    </div>
  );
};

Gallery.propTypes = {
  t: PropTypes.func,
  visible: PropTypes.bool,
  setVisible: PropTypes.func,
  setGalleryContent: PropTypes.func,
  galleryContent: PropTypes.object,
  mediasGroupedByCategoryName: PropTypes.object,
  machineTitle: PropTypes.string,
};

export default withTranslation('common')(Gallery);
